import { X } from 'lucide-react';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { LevelMeter } from '../lib/audio';
import { avatarColor, initials } from '../lib/format';
import { dismissToast, useToasts } from '../lib/toast';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';

export function Button({
  variant = 'secondary',
  size,
  block,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'lg'; block?: boolean }) {
  const cls = ['btn', `btn-${variant}`, size && `btn-${size}`, block && 'btn-block', className].filter(Boolean).join(' ');
  return <button type="button" className={cls} {...rest} />;
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" className="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)} />
  );
}

export function SwitchRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="switch-row">
      <div className="switch-text">
        <span>{label}</span>
        {description && <span className="hint">{description}</span>}
      </div>
      <Switch checked={checked} onChange={onChange} label={label} disabled={disabled} />
    </div>
  );
}

export function Avatar({ name, size = 40, seed }: { name: string; size?: number; seed?: string }) {
  return (
    <span className="avatar" aria-hidden style={{ width: size, height: size, fontSize: size * 0.38, background: avatarColor(seed ?? name) }}>
      {initials(name)}
    </span>
  );
}

// ───────────────────────────── Modal ─────────────────────────────

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
  labelledBy,
}: {
  title?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  labelledBy?: string;
}) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('[autofocus], input, select, textarea, button:not([data-close])');
    (first ?? ref.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
      if (e.key === 'Tab' && ref.current) {
        // Keep keyboard focus inside the dialog.
        const items = [...ref.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
          (el) => !el.hasAttribute('disabled'),
        );
        if (!items.length) return;
        const [firstEl, lastEl] = [items[0], items[items.length - 1]];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      previous?.focus?.();
    };
  }, []);

  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy ?? id} tabIndex={-1}>
        {title && (
          <div className="modal-header">
            <h2 id={id}>{title}</h2>
            <button type="button" className="icon-btn" data-close aria-label="Cerrar" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

// ───────────────────────────── Popover menu ─────────────────────────────

/**
 * Positions a floating menu next to an anchor, flipping above it when there is no room
 * below (the meeting toolbar sits at the bottom of the screen).
 */
export function Popover({
  anchor,
  onClose,
  children,
  placement = 'top',
  label,
}: {
  anchor: RefObject<HTMLElement | null>;
  onClose: () => void;
  children: ReactNode;
  placement?: 'top' | 'bottom';
  label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useLayoutEffect(() => {
    const a = anchor.current?.getBoundingClientRect();
    const m = ref.current?.getBoundingClientRect();
    if (!a || !m) return;
    const margin = 8;
    let top = placement === 'top' ? a.top - m.height - margin : a.bottom + margin;
    if (top < margin) top = a.bottom + margin;
    if (top + m.height > innerHeight - margin) top = Math.max(margin, a.top - m.height - margin);
    let left = a.left + a.width / 2 - m.width / 2;
    left = Math.min(Math.max(margin, left), innerWidth - m.width - margin);
    setPos({ left, top });
  }, [anchor, placement]);

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[role^="menuitem"], button, input')?.focus();
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node) && !anchor.current?.contains(e.target as Node)) onCloseRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        anchor.current?.focus();
      }
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && ref.current) {
        const items = [...ref.current.querySelectorAll<HTMLElement>('[role^="menuitem"]')];
        const i = items.indexOf(document.activeElement as HTMLElement);
        const next = items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
        next?.focus();
        e.preventDefault();
      }
    };
    const onResize = () => onCloseRef.current();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
    };
  }, [anchor]);

  return createPortal(
    <div ref={ref} className="menu" role="menu" aria-label={label} style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}>
      {children}
    </div>,
    document.body,
  );
}

export function MenuItem({
  children,
  onSelect,
  checked,
  danger,
  icon,
  disabled,
}: {
  children: ReactNode;
  onSelect: () => void;
  checked?: boolean;
  danger?: boolean;
  icon?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role={checked === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={checked}
      className={`menu-item ${danger ? 'danger' : ''}`}
      onClick={onSelect}
      disabled={disabled}
    >
      {checked !== undefined ? <span className="check-slot">{checked ? '✓' : ''}</span> : icon}
      <span>{children}</span>
    </button>
  );
}

// ───────────────────────────── Toasts ─────────────────────────────

const TOAST_ICONS = { info: Info, success: CircleCheck, warning: TriangleAlert, error: CircleAlert };

export function Toasts() {
  const items = useToasts();
  return (
    <div className="toasts" role="status" aria-live="polite">
      {items.map((t) => {
        const Icon = TOAST_ICONS[t.kind];
        return (
          <div key={t.id} className={`toast toast-${t.kind}`}>
            <Icon size={18} aria-hidden />
            <span className="toast-text">{t.text}</span>
            {t.action && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  t.action!.run();
                  dismissToast(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
            <button type="button" className="icon-btn" aria-label="Descartar" onClick={() => dismissToast(t.id)}>
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ───────────────────────────── Media helpers ─────────────────────────────

/** Attaches a MediaStream to a <video>. Renders nothing when there is no stream. */
export function VideoView({ stream, mirror, className, muted = true, fit = 'cover' }: { stream: MediaStream | null; mirror?: boolean; className?: string; muted?: boolean; fit?: 'cover' | 'contain' }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.srcObject !== stream) el.srcObject = stream;
    if (stream) el.play().catch(() => undefined);
  }, [stream]);
  return (
    <video
      ref={ref}
      className={className}
      autoPlay
      playsInline
      muted={muted}
      style={{ transform: mirror ? 'scaleX(-1)' : undefined, objectFit: fit }}
    />
  );
}

/** Builds a single-track stream for <video> without re-creating it on every render. */
export function useTrackStream(track: MediaStreamTrack | null | undefined): MediaStream | null {
  const [stream, setStream] = useState<MediaStream | null>(null);
  useEffect(() => {
    setStream(track ? new MediaStream([track]) : null);
  }, [track]);
  return stream;
}

export function LevelBar({ track, bars = 12 }: { track: MediaStreamTrack | null; bars?: number }) {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!track || track.readyState !== 'live') {
      setLevel(0);
      return;
    }
    const meter = new LevelMeter(track);
    let raf = 0;
    const tick = () => {
      setLevel(track.enabled ? meter.level() : 0);
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      meter.dispose();
    };
  }, [track]);
  const on = Math.round(level * bars * 1.6);
  return (
    <div className="level-bar" role="meter" aria-label="Nivel del micrófono" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(level * 100)}>
      {Array.from({ length: bars }, (_, i) => (
        <span key={i} className={i < on ? 'on' : ''} />
      ))}
    </div>
  );
}
